import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import PageHead from "@/components/PageHead";
import { CheckCircle2, AlertCircle } from "lucide-react";

export default function NewsletterConfirm() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [status, setStatus] = useState("loading"); // loading | ok | error
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setMessage("Missing confirmation token.");
      return;
    }
    let mounted = true;
    api
      .get("/newsletter/confirm", { params: { token } })
      .then((r) => {
        if (!mounted) return;
        setStatus("ok");
        setMessage(r.data?.message || "You're subscribed.");
      })
      .catch((e) => {
        if (!mounted) return;
        const detail = e?.response?.data?.detail;
        setStatus("error");
        setMessage(
          typeof detail === "string"
            ? detail
            : "This confirmation link is invalid or has expired."
        );
      });
    return () => {
      mounted = false;
    };
  }, [token]);

  const isOk = status === "ok";

  return (
    <div data-testid="newsletter-confirm-page">
      <PageHead
        title="Confirm your Redkey subscription"
        description="Confirm your subscription to the Redkey Web Design blog."
      />
      <section className="mx-auto max-w-[640px] px-6 md:px-10 py-24">
        <p className="eyebrow">§ Newsletter · Confirmation</p>

        {status === "loading" && (
          <p
            className="mt-8 font-body text-ink-soft"
            data-testid="newsletter-confirm-loading"
          >
            Confirming your subscription…
          </p>
        )}

        {status !== "loading" && (
          <>
            <div className="mt-6 flex items-start gap-4">
              <div
                className={`shrink-0 mt-2 h-10 w-10 rounded-full flex items-center justify-center ${
                  isOk ? "bg-brand-red text-paper" : "bg-paper-alt text-brand-red-ink border hairline-strong"
                }`}
              >
                {isOk ? <CheckCircle2 size={20} /> : <AlertCircle size={20} />}
              </div>
              <div>
                <h1
                  className="font-display text-4xl md:text-5xl leading-[1.02]"
                  data-testid={isOk ? "newsletter-confirm-ok" : "newsletter-confirm-error"}
                >
                  {isOk ? (
                    <>
                      You&rsquo;re <em className="not-italic text-brand-red">in.</em>
                    </>
                  ) : (
                    <>
                      Confirmation <em className="not-italic text-brand-red">failed.</em>
                    </>
                  )}
                </h1>
                <p className="mt-4 font-body text-ink-soft max-w-prose leading-relaxed">
                  {message}
                </p>
              </div>
            </div>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/blog"
                data-testid="newsletter-confirm-blog"
                className="inline-flex items-center gap-2 bg-ink text-paper px-6 py-3 hover:bg-electric transition-colors font-ui"
              >
                Browse the blog <span aria-hidden>→</span>
              </Link>
              <Link
                to="/"
                className="inline-flex items-center gap-2 border hairline-strong px-6 py-3 hover:border-ink font-ui"
              >
                Back to home
              </Link>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
